import React, { FC, useMemo } from "react";
import { HiddenVisually, Text } from "@bookingcom/bui-react";
import { useI18n, I18nChildContext } from "@bookingcom/lingojs-react";
import { t } from "@bookingcom/lingojs-core";
import { UITimelineLeg } from "@flights/types";
import { SPACER_PADDED } from "@flights/constants";
import useCabinClassName from "../../../../hooks/useCabinClassName";
import Frame from "../../Frame";
import styles from "./TimelineLegInfo.module.css";
import useSearchCabinClass from "../../../../hooks/useSearchCabinClass";
import TimelineLegAmenities from "./TimelineLegAmenities";
import useLegAmenities from "../../../../hooks/useLegAmenities";
import { formattingUtils } from "@flights/web-api-utils-universal";

// flights_web_route_happy_amenities_ddot
// flights_web_route_happy_amenities_mdot

export type AmenityProps = {
  showAmenities?: boolean;
  amenitiesVariant?: "popover" | "modal" | "inline";
  onAmenitiesPopoverOpen?: VoidFunction;
};

export const TimelineLegInfo: FC<{ leg: UITimelineLeg } & AmenityProps> = ({
  leg,
  showAmenities,
  amenitiesVariant,
  onAmenitiesPopoverOpen
}) => {
  const i18n = useI18n() as I18nChildContext;
  const cabinClassName = useCabinClassName();
  const { searchCabinClass } = useSearchCabinClass();
  const { getAmenities, getAircraftModel } = useLegAmenities(leg.amenities);

  const { carrier, carrierLogo, cabinClass, operatingCarrier, operatingCarrierDisclosureText, flightDurationMinutes } =
    leg;

  const isMismatchCabinClass = useMemo(
    () => !!searchCabinClass && searchCabinClass !== cabinClass,
    [searchCabinClass, cabinClass]
  );

  const aircraftModel = getAircraftModel();

  return (
    <Frame direction="row">
      <Frame className={styles.logoColumn} attributes={{ "data-testid": "timeline_leg_info_logo" }}>
        <img role="presentation" alt={carrier} src={carrierLogo} className={styles.carrierLogoImg} />
      </Frame>
      <Frame className={styles.textColumn}>
        <Text variant="small_1" attributes={{ "data-testid": "timeline_leg_info_carrier" }}>
          {carrier}
          {showAmenities && aircraftModel && (
            <>
              {SPACER_PADDED}
              {getLegFullFlightNumber(leg)}
            </>
          )}
        </Text>
        {operatingCarrierDisclosureText && (
          <Text variant="small_1">
            {i18n.trans(
              t("flights_airline_operated_by", {
                variables: {
                  airline_name: operatingCarrierDisclosureText
                }
              })
            )}
          </Text>
        )}
        {!operatingCarrierDisclosureText && operatingCarrier && (
          <Text variant="small_1">
            {i18n.trans(
              t("flights_airline_operated_by", {
                variables: {
                  airline_name: operatingCarrier
                }
              })
            )}
          </Text>
        )}

        <Text variant="small_1" attributes={{ "data-testid": "timeline_leg_info_flight_number_and_class" }}>
          {(!showAmenities || !aircraftModel) && getLegFullFlightNumber(leg)}
          {(!showAmenities || !aircraftModel) && SPACER_PADDED}
          {isMismatchCabinClass ? (
            <span style={{ backgroundColor: "var(--bui_color_callout_background_alt)" }}>
              {cabinClassName(cabinClass)}
            </span>
          ) : (
            cabinClassName(cabinClass)
          )}
          {showAmenities && aircraftModel && (
            <>
              {SPACER_PADDED}
              {getAircraftModel()}
            </>
          )}
          {/*eslint-disable-next-line flights/no-unassigned-todo-comments*/}
          {/* TODO(asamilyak): support also plane type here */}
        </Text>

        <Text
          variant="small_1"
          attributes={{
            "data-testid": "timeline_leg_info_duration",
            "aria-hidden": "true"
          }}
        >
          {i18n.trans(
            t("flights_itinerary_duration", {
              variables: {
                duration: formattingUtils.flightDuration(flightDurationMinutes, i18n)
              }
            })
          )}
        </Text>
        <HiddenVisually>
          {i18n.trans(
            t("flights_itinerary_duration", {
              variables: {
                duration: formattingUtils.flightDurationAria(flightDurationMinutes, i18n)
              }
            })
          )}
        </HiddenVisually>

        {showAmenities && (
          <TimelineLegAmenities
            amenities={getAmenities()}
            variant={amenitiesVariant}
            onPopoverOpen={onAmenitiesPopoverOpen}
          />
        )}
      </Frame>
    </Frame>
  );
};

const getLegFullFlightNumber = (leg: UITimelineLeg): string => {
  /*eslint-disable-next-line flights/no-unassigned-todo-comments*/
  /* TODO(asamilyak): do we have a utility function for that? */
  return `${leg.carrierCode}${leg.flightNumber}`;
};
